/**
 * Bottom dock panel showing per-node detail of a single execution.
 * Used in both Editor mode (after a test-run) and Executions mode
 * (browsing past runs).
 *
 * Layout — mirrors n8n's Logs dock:
 *
 *   ━━━━━ ⫶ drag-handle ⫶ ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
 *   wfr_<id>   [STATUS]   timestamp        Logs · <id> · [STATUS]  ▾
 *   ──────────────────┬──────────────────────────────────────────────
 *    • Manual          │ Manual · SUCCEEDED · 12ms     [Input|Output]
 *    • Condition       │ ┌── ItemBatchViewer (Schema/Table/JSON/Binary)
 *    • ⚪ Extract Text │ │
 *   ──────────────────┴──────────────────────────────────────────────
 *
 * Design choices:
 *   * **Bottom dock, not overlay** — the caller is expected to host
 *     the panel inside a flex-column where it splits the vertical
 *     space with the canvas, so the canvas shrinks when the panel
 *     grows. No more "logs hiding the bottom of the graph".
 *   * **Resizable** — drag the top edge to grow/shrink. Height is
 *     owned by the parent so it can persist via localStorage.
 *   * **Collapsible** — chevron in the header drops the body and
 *     leaves only the header strip visible.
 *   * **ItemBatchViewer for payloads** — single shared lens (Schema /
 *     Table / JSON / Binary). No more bespoke duplicate viewer here.
 */
import {
  CheckCircle2,
  ChevronDown,
  ChevronUp,
  Circle,
  GripHorizontal,
  Loader2,
  XCircle,
} from "lucide-react";
import * as React from "react";

import { Button } from "@/components/ui/button";
import { ItemBatchViewer } from "@/components/workflows/ItemBatchViewer";
import { RunSelector } from "@/components/workflows/RunSelector";
import { runSlicesOf, sliceRun } from "@/lib/workflows/nodeRuns";
import {
  SubWorkflowRunDialog,
  subWfPropsFromNode,
} from "@/components/workflows/SubWorkflowRunDialog";
import type { ExecutionDetail, NodeExecutionSummary } from "@/lib/api-workflows";
import { cn } from "@/lib/cn";


export interface RunDetailsPanelProps {
  /** The run to display. ``null`` hides the panel entirely. */
  run: ExecutionDetail | null;
  /** Currently-focused node id (synced with the canvas's selected node
   *  so clicking a node on the graph highlights the matching row). */
  selectedNodeId: string | null;
  /** Set when the user clicks a row in the left list. Parent uses this
   *  to ALSO focus the node on the canvas. */
  onSelectNode: (nodeId: string) => void;
  /** Open / collapsed state lives in the parent so it can persist
   *  across selection changes. */
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Current height (in px) when open. Parent owns it so it can
   *  persist (localStorage). */
  height: number;
  /** Called while dragging the resize handle. Caller is expected to
   *  clamp / persist. */
  onHeightChange: (h: number) => void;
  /** Optional Replay button — when provided, shows in the header. */
  onReplay?: () => void;
  /** The execution currently running live (from the editor's SSE listener).
   *  Passed to the sub-workflow drill-in so it can overlay child node status
   *  LIVE during a run (the child's events ride the parent execution_id). */
  liveExecutionId?: string | null;
}


const MIN_OPEN_HEIGHT = 180;
const MAX_OPEN_HEIGHT = 900; // hard cap; effective max enforced by parent against viewport


type PaneTab = "input" | "output";


// ---------------------------------------------------------------------------
// Sub-workflow drill-down (ADR-0013 addendum)
// ---------------------------------------------------------------------------
//
// A ``call_workflow`` node carries the child's per-node traces in
// ``log_outputs.sub_traces`` (aggregated on the backend). We FLATTEN them into
// the node list as INDENTED "child" rows so the operator sees the sub-workflow's
// internals in the parent's Logs panel — clearly marked as belonging to another
// workflow. Nested sub-workflows recurse (a child's own call_workflow rides its
// own ``sub_traces``).

export interface LogRow {
  /** Unique key across the whole flattened tree (parent id ▸ child node id …). */
  key: string;
  node: NodeExecutionSummary;
  /** 0 = a top-level node of THIS workflow; 1+ = a node of a nested sub-workflow. */
  depth: number;
  /** The sub-workflow name this child row belongs to (set on depth ≥ 1). */
  subWorkflowName?: string;
}

function subTraceToSummary(t: Record<string, unknown>, key: string): NodeExecutionSummary {
  return {
    id: key,
    node_id: String(t.node_id ?? ""),
    node_kind: (t.node_kind ?? "") as NodeExecutionSummary["node_kind"],
    status: (t.status ?? "succeeded") as NodeExecutionSummary["status"],
    iteration: 0,
    duration_ms: typeof t.duration_ms === "number" ? t.duration_ms : null,
    error: (t.error as string | null) ?? null,
    started_at: "",
    finished_at: null,
    input: t.input,
    output: t.output,
    log_outputs: (t.log_outputs ?? null) as Record<string, unknown> | null,
  };
}

/** Flatten the top-level node list, inlining each call_workflow node's
 *  ``sub_traces`` as indented child rows (recursively for nested sub-workflows). */
export function flattenRows(nodes: NodeExecutionSummary[]): LogRow[] {
  const rows: LogRow[] = [];
  const walk = (node: NodeExecutionSummary, depth: number, key: string, subName?: string) => {
    rows.push({ key, node, depth, subWorkflowName: subName });
    const subs = node.log_outputs?.sub_traces;
    const childName =
      (node.log_outputs?.sub_workflow_name as string | undefined) || "sub-workflow";
    if (Array.isArray(subs)) {
      subs.forEach((t, i) => {
        const child = t as Record<string, unknown>;
        const childKey = `${key}▸${String(child.node_id ?? i)}`;
        walk(subTraceToSummary(child, childKey), depth + 1, childKey, childName);
      });
    }
  };
  nodes.forEach((n, i) => walk(n, 0, n.id || `n${i}`));
  return rows;
}


export function RunDetailsPanel({
  run,
  selectedNodeId,
  onSelectNode,
  open,
  onOpenChange,
  height,
  onHeightChange,
  onReplay,
  liveExecutionId,
}: RunDetailsPanelProps) {
  const [paneTab, setPaneTab] = React.useState<PaneTab>("output");
  // A clicked ROW key (unique across the flattened tree). Lets a sub-workflow
  // CHILD row be selected even though its node_id isn't on the parent canvas.
  const [selectedKey, setSelectedKey] = React.useState<string | null>(null);
  // A CANVAS node click (``selectedNodeId``) must win over a stale panel-row
  // selection — otherwise ``focusedRow`` keeps showing the old ``selectedKey``
  // row and clicking a node on the canvas never updates the shown I/O. Clear
  // the internal key whenever the canvas selection changes; a subsequent
  // in-panel row click re-sets it (and wins until the next canvas click). A
  // depth-0 row click also drives ``selectedNodeId`` to the SAME node, so this
  // reset is a no-op for it; a sub-workflow CHILD row click leaves
  // ``selectedNodeId`` unchanged, so its key survives.
  React.useEffect(() => {
    setSelectedKey(null);
  }, [selectedNodeId]);
  // The child-run drill-in modal (read-only sub-workflow canvas + overlay).
  const [drillNode, setDrillNode] = React.useState<NodeExecutionSummary | null>(null);

  // Drag-to-resize: store the starting mouse Y + starting height so
  // the move handler computes new height as a delta. We attach listeners
  // to the window (not the handle) so the drag keeps working when the
  // cursor leaves the panel.
  const dragStateRef = React.useRef<{ startY: number; startH: number } | null>(
    null,
  );
  React.useEffect(() => {
    function onMove(e: MouseEvent) {
      const s = dragStateRef.current;
      if (!s) return;
      // Drag UP = grow, drag DOWN = shrink (handle is on top).
      const next = Math.min(
        MAX_OPEN_HEIGHT,
        Math.max(MIN_OPEN_HEIGHT, s.startH + (s.startY - e.clientY)),
      );
      onHeightChange(next);
    }
    function onUp() {
      dragStateRef.current = null;
      document.body.style.cursor = "";
      document.body.style.userSelect = "";
    }
    window.addEventListener("mousemove", onMove);
    window.addEventListener("mouseup", onUp);
    return () => {
      window.removeEventListener("mousemove", onMove);
      window.removeEventListener("mouseup", onUp);
    };
  }, [onHeightChange]);

  function startDrag(e: React.MouseEvent) {
    e.preventDefault();
    dragStateRef.current = { startY: e.clientY, startH: height };
    // Lock cursor + disable selection during the drag for the whole
    // page — otherwise the user accidentally text-selects everything.
    document.body.style.cursor = "row-resize";
    document.body.style.userSelect = "none";
  }

  if (!run) return null;

  // Flatten top-level nodes + their sub-workflow child traces into one list.
  const rows = flattenRows(run.nodes);
  // Focused row: an explicit row click wins; else the canvas-selected top-level
  // node; else the first row.
  const focusedRow: LogRow | null =
    rows.find((r) => r.key === selectedKey) ||
    (selectedNodeId
      ? rows.find((r) => r.depth === 0 && r.node.node_id === selectedNodeId)
      : undefined) ||
    rows[0] ||
    null;
  const focused = focusedRow?.node ?? null;

  // When collapsed, let the dock hug its header exactly (height: auto) rather
  // than a fixed pixel value — a hardcoded height clipped the header on some
  // zoom/font settings. Open state keeps the user-dragged pixel height.
  const collapsedStyle = open ? { height } : {};

  return (
    <div
      className="flex shrink-0 flex-col border-t border-border bg-surface"
      style={collapsedStyle}
    >
      {/* Resize handle — only active when open. Sits on the very top
          edge of the dock and shows a row-resize cursor on hover. */}
      {open && (
        <div
          onMouseDown={startDrag}
          className="group flex h-1.5 shrink-0 cursor-row-resize items-center justify-center bg-border/40 transition hover:bg-accent"
          title="Drag to resize"
        >
          <GripHorizontal
            className="h-2.5 w-2.5 text-muted-foreground/40 group-hover:text-foreground"
            strokeWidth={2}
          />
        </div>
      )}

      {/* Top header: run id + status + timestamp + replay + chevron.
          Every element is ``shrink-0`` / ``whitespace-nowrap`` EXCEPT the
          run-id (the sole ``truncate`` flex item) — otherwise on a narrow
          dock the status badge / timestamp wrap to a second line and get
          clipped by the 40px collapsed height. */}
      <div className="flex shrink-0 items-center justify-between gap-2 border-b border-border/60 px-3 py-1.5 text-[11px]">
        <div className="flex min-w-0 items-center gap-2">
          <button
            type="button"
            onClick={() => onOpenChange(!open)}
            className="shrink-0 text-muted-foreground hover:text-foreground"
            aria-label={open ? "Collapse logs" : "Expand logs"}
            title={open ? "Collapse logs" : "Expand logs"}
          >
            {open ? (
              <ChevronDown className="h-3.5 w-3.5" />
            ) : (
              <ChevronUp className="h-3.5 w-3.5" />
            )}
          </button>
          <span className="shrink-0 text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">
            Logs
          </span>
          <span className="shrink-0 text-muted-foreground">·</span>
          <span className="truncate font-mono text-foreground">
            wfr_{run.id.slice(0, 10).toUpperCase()}
          </span>
          <StatusBadge status={run.status} />
          <span className="shrink-0 whitespace-nowrap text-muted-foreground">
            {new Date(run.started_at).toLocaleString()}
          </span>
        </div>
        <div className="flex items-center gap-1">
          {onReplay && (
            <Button size="sm" variant="outline" onClick={onReplay}>
              Replay
            </Button>
          )}
        </div>
      </div>

      {/* Body: 2-column. Hidden when collapsed. */}
      {open && (
        <div className="flex min-h-0 flex-1">
          {/* LEFT — node list. ``min-h-0`` + ``pb-2`` so the scroll area
              bounds correctly and the LAST node row isn't clipped flush
              against the dock's bottom edge. */}
          <div className="w-56 shrink-0 overflow-y-auto border-r border-border/60 min-h-0 pb-2">
            {rows.length === 0 ? (
              <div className="px-3 py-3 text-[11px] text-muted-foreground">
                No node traces recorded.
              </div>
            ) : (
              rows.map((row) => {
                const nx = row.node;
                const isChild = row.depth > 0;
                return (
                  <button
                    key={row.key}
                    type="button"
                    onClick={() => {
                      setSelectedKey(row.key);
                      // Only a top-level node exists on the parent canvas —
                      // sync the canvas highlight for those; a sub-workflow
                      // child row just shows its detail.
                      if (row.depth === 0) onSelectNode(nx.node_id);
                    }}
                    className={cn(
                      "flex w-full items-center gap-2 border-l-2 px-3 py-1.5 text-left text-[11px] transition hover:bg-muted/40",
                      focusedRow?.key === row.key
                        ? "border-l-foreground bg-muted/50"
                        : "border-l-transparent",
                    )}
                    style={isChild ? { paddingLeft: 12 + row.depth * 16 } : undefined}
                    title={
                      isChild
                        ? `${nx.node_id} — sub-workflow "${row.subWorkflowName}"`
                        : nx.node_id
                    }
                  >
                    {/* A child row draws a subtle "belongs to a sub-workflow"
                        connector so it reads as nested, not a sibling. */}
                    {isChild && (
                      <span className="shrink-0 font-mono text-[10px] text-muted-foreground/60">
                        ⤷
                      </span>
                    )}
                    <StatusDot status={nx.status} />
                    <span
                      className={cn(
                        "truncate",
                        isChild ? "text-muted-foreground" : "text-foreground",
                      )}
                    >
                      {nx.node_id}
                    </span>
                    {nx.iteration > 0 && (
                      <span className="font-mono text-[9px] text-muted-foreground">
                        #{nx.iteration}
                      </span>
                    )}
                    {nx.duration_ms != null && (
                      <span className="ml-auto font-mono text-[10px] text-muted-foreground">
                        {nx.duration_ms}ms
                      </span>
                    )}
                  </button>
                );
              })
            )}
          </div>

          {/* RIGHT — selected node detail. ``min-h-0`` is REQUIRED: without
              it this flex child can't shrink below its content height, so the
              inner ``overflow-auto`` payload pane never scrolls and long
              outputs get clipped instead of scrolling. */}
          <div className="flex min-h-0 min-w-0 flex-1 flex-col">
            {focused ? (
              <NodeDetail
                node={focused}
                paneTab={paneTab}
                onPaneTab={setPaneTab}
                executionId={run.id}
                subWorkflowName={focusedRow?.subWorkflowName}
                onDrillIn={hasSubTraces(focused) ? () => setDrillNode(focused) : undefined}
              />
            ) : (
              <div className="m-3 text-[11px] text-muted-foreground">
                Pick a node on the left.
              </div>
            )}
          </div>
        </div>
      )}

      {/* Sub-workflow drill-in — read-only canvas of the child run (ADR-0013).
          Overlays LIVE status from the run in flight (falls back to the shown
          run's id) so re-running with the dialog open lights the child up live. */}
      {drillNode && (
        <SubWorkflowRunDialog
          {...subWfPropsFromNode(drillNode)}
          executionId={liveExecutionId ?? run.id}
          onClose={() => setDrillNode(null)}
        />
      )}
    </div>
  );
}


/** True when a node carries a non-empty sub-workflow trace list. */
/** Item count of a serialised batch, or "—" when the payload isn't a list
 *  (not captured, or a truncation marker). Drives the INPUT/OUTPUT tab counts. */
export function batchCount(payload: unknown): string {
  return Array.isArray(payload) ? String(payload.length) : "—";
}

function hasSubTraces(node: NodeExecutionSummary): boolean {
  const subs = node.log_outputs?.sub_traces;
  return Array.isArray(subs) && subs.length > 0;
}


// ---------------------------------------------------------------------------
// Right-pane detail
// ---------------------------------------------------------------------------


function NodeDetail({
  node,
  paneTab,
  onPaneTab,
  executionId,
  subWorkflowName,
  onDrillIn,
}: {
  node: NodeExecutionSummary;
  paneTab: PaneTab;
  onPaneTab: (t: PaneTab) => void;
  executionId: string;
  /** Set when this node is a CHILD of a sub-workflow (renders a "in <name>" badge). */
  subWorkflowName?: string;
  /** Set when this node is a call_workflow with child traces → shows a drill-in button. */
  onDrillIn?: () => void;
}) {
  const payload = paneTab === "input" ? node.input : node.output;
  // A node inside a loop ran once per ITERATION — page them here too, so the
  // Logs dock answers "which iteration?" like the node drawer does.
  const runs = React.useMemo(() => runSlicesOf(node.log_outputs), [node.log_outputs]);
  const [selectedRun, setSelectedRun] = React.useState<number | null>(null);
  React.useEffect(() => setSelectedRun(null), [node.node_id]);
  const activeRun = runs?.find((r) => r.run === selectedRun) ?? null;
  const shownPayload = activeRun
    ? sliceRun(
        payload,
        paneTab === "input" ? activeRun.in_offset : activeRun.out_offset,
        paneTab === "input" ? activeRun.items_in : activeRun.items_out,
      )
    : payload;

  return (
    <div className="flex h-full min-h-0 flex-col">
      {/* Header: node name + status + Input/Output toggle */}
      <div className="flex shrink-0 items-center justify-between gap-2 border-b border-border/60 px-3 py-1.5">
        <div className="flex min-w-0 items-center gap-2 text-[12px]">
          <span className="truncate font-medium text-foreground">{node.node_id}</span>
          {subWorkflowName && (
            <span
              className="inline-flex shrink-0 items-center gap-1 rounded bg-info/10 px-1.5 py-0.5 text-[9px] font-medium uppercase tracking-wide text-info"
              title={`Nodo del sub-workflow "${subWorkflowName}"`}
            >
              ⤷ {subWorkflowName}
            </span>
          )}
          <span className="text-muted-foreground">·</span>
          <span
            className={cn(
              "text-[11px] uppercase",
              node.status === "failed"
                ? "text-destructive"
                : node.status === "skipped"
                  ? "text-muted-foreground"
                  : "text-success",
            )}
          >
            {node.status}
          </span>
          {node.duration_ms != null && (
            <span className="text-[10px] text-muted-foreground">
              · {node.duration_ms}ms
            </span>
          )}
          {onDrillIn && (
            <Button
              size="sm"
              variant="outline"
              className="ml-1 h-6 shrink-0 px-2 text-[10px]"
              onClick={onDrillIn}
            >
              Apri sub-workflow ▸
            </Button>
          )}
        </div>
        {/* The item COUNT on each tab is load-bearing, not decoration: a
            passthrough node (wait, trigger) has input === output byte-for-byte,
            so without it the switcher looks BROKEN — you click and nothing
            changes. The counts say "25 → 25" and the identity becomes a fact
            you can read instead of a bug you suspect. */}
        <div className="inline-flex rounded-md border border-border bg-background p-0.5">
          {(["input", "output"] as PaneTab[]).map((t) => (
            <button
              key={t}
              type="button"
              onClick={() => onPaneTab(t)}
              className={cn(
                "flex items-center gap-1 rounded px-2 py-0.5 text-[10px] font-medium uppercase transition",
                paneTab === t
                  ? "bg-foreground text-background"
                  : "text-muted-foreground hover:text-foreground",
              )}
            >
              {t}
              <span className={cn("font-mono", paneTab === t ? "opacity-70" : "opacity-50")}>
                {batchCount(t === "input" ? node.input : node.output)}
              </span>
            </button>
          ))}
        </div>
      </div>

      {/* Error banner — shown above the payload pane for failed nodes */}
      {node.error && (
        <div className="border-b border-border/60 bg-destructive/10 px-3 py-1.5 font-mono text-[11px] text-destructive">
          {node.error}
        </div>
      )}

      {/* Payload pane — delegates rendering to the shared multi-view
          ItemBatchViewer so Schema/Table/JSON/Binary all behave the
          same way they do in the node editor modal. */}
      <div className="flex-1 overflow-auto p-3">
        {runs && (
          <RunSelector
            runs={runs}
            logOutputs={node.log_outputs}
            selected={selectedRun}
            onSelect={setSelectedRun}
            side={paneTab === "input" ? "in" : "out"}
            className="mb-2"
          />
        )}
        {activeRun?.error && paneTab === "output" && (
          <div className="mb-2 rounded border border-danger/40 bg-danger/10 px-2 py-1 font-mono text-[10px] text-danger">
            {activeRun.error}
          </div>
        )}
        {shownPayload === undefined || shownPayload === null ? (
          <div className="text-[11px] text-muted-foreground">
            ({paneTab} not captured for this trace)
          </div>
        ) : (
          <ItemBatchViewer
            key={selectedRun ?? "all"}
            payload={shownPayload}
            blobContext={{
              executionId,
              nodeId: node.node_id,
              io: paneTab === "input" ? "input" : "output",
            }}
          />
        )}
      </div>
    </div>
  );
}


// ---------------------------------------------------------------------------
// Status helpers
// ---------------------------------------------------------------------------


function StatusBadge({ status }: { status: string }) {
  if (status === "succeeded") {
    return (
      <span className="inline-flex shrink-0 items-center gap-1 whitespace-nowrap rounded bg-success/10 px-1.5 py-0.5 text-[10px] font-medium uppercase text-success">
        <CheckCircle2 className="h-3 w-3" strokeWidth={2} />
        succeeded
      </span>
    );
  }
  if (status === "failed") {
    return (
      <span className="inline-flex shrink-0 items-center gap-1 whitespace-nowrap rounded bg-destructive/10 px-1.5 py-0.5 text-[10px] font-medium uppercase text-destructive">
        <XCircle className="h-3 w-3" strokeWidth={2} />
        failed
      </span>
    );
  }
  if (status === "running") {
    return (
      <span className="inline-flex shrink-0 items-center gap-1 whitespace-nowrap rounded bg-info/10 px-1.5 py-0.5 text-[10px] font-medium uppercase text-info">
        <Loader2 className="h-3 w-3 animate-spin" strokeWidth={2} />
        running
      </span>
    );
  }
  return (
    <span className="inline-flex shrink-0 items-center gap-1 whitespace-nowrap rounded bg-muted px-1.5 py-0.5 text-[10px] font-medium uppercase text-muted-foreground">
      {status}
    </span>
  );
}


function StatusDot({ status }: { status: string }) {
  if (status === "succeeded")
    return <CheckCircle2 className="h-3 w-3 shrink-0 text-success" strokeWidth={2} />;
  if (status === "failed")
    return <XCircle className="h-3 w-3 shrink-0 text-destructive" strokeWidth={2} />;
  if (status === "running")
    return (
      <Loader2 className="h-3 w-3 shrink-0 animate-spin text-info" strokeWidth={2} />
    );
  return <Circle className="h-3 w-3 shrink-0 text-muted-foreground/50" strokeWidth={2} />;
}
